'use client';

import { SWRConfig } from 'swr';
import React from 'react';
import logger from '@/utils/client-logger';
import { toast } from '@/components/Toast';
import { performLogout } from '@/utils/logout';
import { httpRequest } from '@/utils/axios';

interface SWRProviderProps {
  children: React.ReactNode;
}

/**
 * 全局默认 Fetcher 兜底
 * 当 useSWR 没有提供具体的 fetcher 时，会默认使用此函数。
 * 仅支持标准接口格式，如 `useSWR('GET:/api/xxx')` 或 `useSWR(['POST:/api/xxx', { data: 1 }])`
 */
const globalFetcher = async (key: string | any[]) => {
  // 如果 SWR Key 是被 serializeKey 转换过的字符串，需要先解析回数组
  let parsedKey: any = key;
  if (typeof key === 'string' && key.startsWith('[')) {
    try {
      parsedKey = JSON.parse(key);
    } catch (e) {
      // 解析失败则当做普通字符串处理
    }
  }

  // 处理字符串类型的 URL
  if (
    typeof parsedKey === 'string' &&
    (parsedKey.startsWith('GET:') || parsedKey.startsWith('POST:'))
  ) {
    const res = await httpRequest(parsedKey as any);
    return (res as any)?.data ?? res;
  }

  // 处理带参数的数组类型 [url, params]
  if (
    Array.isArray(parsedKey) &&
    typeof parsedKey[0] === 'string' &&
    (parsedKey[0].startsWith('GET:') || parsedKey[0].startsWith('POST:'))
  ) {
    const res = await httpRequest(parsedKey[0] as any, parsedKey[1]);
    return (res as any)?.data ?? res;
  }

  // 对于领域驱动的 Key（如 ['home', 'blocks']），全局 Fetcher 无法处理，抛出提示
  throw new Error(
    `SWR 全局 Fetcher 无法处理该格式的 Key: ${JSON.stringify(key)}。请在 useSWR 中手动传入对应的 fetcher。`,
  );
};

/**
 * 全局 SWR 兜底配置 Provider
 * 为整个应用的 SWR 调用提供统一的行为规范、错误处理和默认 Fetcher
 */
export function SWRProvider({ children }: SWRProviderProps) {
  return (
    <SWRConfig
      value={{
        // 1. 行为策略 (偏保守，防止过度请求导致页面闪烁或服务器压力)
        revalidateOnFocus: false, // 切换 Tab 不默认刷新 (电商网站很讨厌一切回来商品就闪烁)
        revalidateOnReconnect: true, // 断网重连时刷新
        shouldRetryOnError: true,
        errorRetryCount: 3, // 错误重试 3 次
        dedupingInterval: 2000, // 2秒内同样的请求只发一次 (防组件重复挂载风暴)

        // 2. 默认网络请求引擎
        fetcher: globalFetcher,

        // 3. 全局错误接管
        onError: (error, key) => {
          logger.error(`SWR Fetch Error [${JSON.stringify(key)}]:`, error);

          // Axios 抛出的 error 对象可能会带有 response 属性
          const status = error.status || error.response?.status;

          if (status === 401) {
            toast.error(error.message || '登录已过期，请重新登录');
            performLogout({
              shouldCallLogouApi: false,
            });
            return;
          }

          if (status === 403) {
            toast.error('您没有权限执行此操作');
            return;
          }

          if (status >= 500) {
            toast.error('网络繁忙，请稍后再试');
          }
        },
      }}
    >
      {children}
    </SWRConfig>
  );
}
